import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';

/** Count colour: `alert` = a person must act (red); `muted` = information (grey). */
export type NavCountTone = 'alert' | 'muted';

export interface NavItemProps {
  icon: IconKind;
  label: string;
  active?: boolean;
  /** Numeric count. Zero renders nothing; capped at 99+. */
  count?: number;
  /** Colour of the count. Defaults to `muted` (grey). */
  countTone?: NavCountTone;
  /** Not-purchased: rendered muted with a padlock; still clickable (opens explainer). */
  locked?: boolean;
  /** Back-compat: prefer `count`/`countTone`. Numeric grey badge. */
  badge?: number;
  /** Back-compat: prefer `count`/`countTone`. Red text badge. */
  badgeText?: string;
  sparkle?: boolean;
  live?: boolean;
  /** Amber "on hold" dot in the top-right corner (spec 36 §7: Messages / Email when
   * their agent is held). Shown in both the expanded row and the collapsed rail,
   * and left intact alongside any unread count. */
  heldDot?: boolean;
  compact?: boolean;
  onClick?: () => void;
  className?: string;
}

function formatCount(n: number): string {
  return n > 99 ? '99+' : String(n);
}

export function NavItem({
  icon,
  label,
  active,
  count,
  countTone = 'muted',
  locked,
  badge,
  badgeText,
  sparkle,
  live,
  heldDot,
  compact,
  onClick,
  className,
}: NavItemProps) {
  const [hovered, setHovered] = useState(false);

  // Back-compat: fold the old badge/badgeText props into the count model.
  const resolvedCount =
    count ?? badge ?? (badgeText !== undefined ? Number.parseInt(badgeText, 10) : undefined);
  const resolvedTone: NavCountTone =
    count !== undefined ? countTone : badgeText !== undefined ? 'alert' : countTone;
  const hasCount = resolvedCount !== undefined && !Number.isNaN(resolvedCount) && resolvedCount > 0;
  const countLabel = resolvedCount !== undefined ? formatCount(resolvedCount) : '';

  const alert = resolvedTone === 'alert';

  return (
    <button
      type="button"
      title={compact ? label : undefined}
      onClick={onClick}
      onMouseEnter={() => {
        setHovered(true);
      }}
      onMouseLeave={() => {
        setHovered(false);
      }}
      className={cn(
        'relative w-full cursor-pointer overflow-hidden rounded-[var(--r-md)] text-left outline-none transition-[background,box-shadow,border-color] duration-[160ms] ease-[var(--ease-out)]',
        compact
          ? 'flex items-center justify-center px-0 py-2.5'
          : 'grid grid-cols-[18px_1fr_auto] items-center gap-3 px-[11px] py-2 text-[13.5px] font-medium',
        active ? 'font-semibold text-white' : 'text-[var(--sidebar-ink)]',
        locked && !active && 'opacity-70',
        className,
      )}
      style={
        active
          ? {
              background: 'var(--nav-active-bg)',
              border: '1px solid var(--nav-active-border)',
              boxShadow: 'var(--nav-active-shadow)',
            }
          : hovered
            ? { background: 'var(--nav-hover-bg)', border: '1px solid var(--nav-hover-border)' }
            : { background: 'transparent', border: '1px solid transparent' }
      }
    >
      {/* Design system rev 1: the active pill is a flat brand-indigo fill — no gloss. */}
      <Icon
        kind={icon}
        size={sparkle ? 14 : 16}
        stroke={sparkle ? 2 : 1.7}
        color={active ? '#fff' : 'var(--sidebar-icon)'}
      />

      {/* Collapsed rail: red alert counts become a corner dot; grey counts disappear. */}
      {compact && hasCount && alert && !active && (
        <span
          aria-hidden
          className="absolute right-[10px] top-[7px] h-[7px] w-[7px] rounded-full bg-[var(--terracotta)] ring-2 ring-[var(--sidebar-bg,transparent)]"
        />
      )}
      {compact && locked && (
        <span
          aria-hidden
          className="absolute right-[9px] top-[7px] inline-flex items-center justify-center"
        >
          <Icon kind="lock" size={10} stroke={2} color="var(--sidebar-muted)" />
        </span>
      )}

      {/* On-hold dot (spec 36 §7): an amber corner flag that sits above any unread
          count, in both the expanded row and the collapsed rail. */}
      {heldDot && (
        <span
          aria-hidden
          className="absolute right-[7px] top-[6px] h-[7px] w-[7px] rounded-full ring-2 ring-[var(--sidebar-bg,transparent)]"
          style={{ background: active ? '#fff' : 'var(--gold)' }}
        />
      )}

      {!compact && (
        <>
          <span>{label}</span>
          {active && hasCount ? (
            // Active + count: the count inverts onto the fill.
            <span className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-white/25 px-1.5 text-[10px] font-bold leading-[16px] text-white">
              {countLabel}
            </span>
          ) : active ? (
            <span className="h-[5px] w-[5px] rounded-[3px] bg-white" />
          ) : locked ? (
            <Icon kind="lock" size={13} stroke={1.9} color="var(--sidebar-muted)" />
          ) : hasCount ? (
            <span
              className={cn(
                'inline-flex min-w-[18px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold leading-[16px]',
                alert
                  ? 'bg-[var(--terracotta-tint)] text-[var(--terracotta)]'
                  : 'bg-[var(--sidebar-count-muted-bg,var(--ws-card-bg))] text-[var(--sidebar-muted)]',
              )}
            >
              {countLabel}
            </span>
          ) : live ? (
            <span className="elva-live h-[6px] w-[6px]" aria-hidden />
          ) : (
            <span />
          )}
        </>
      )}
    </button>
  );
}
